import type { DOMAdapter } from '../types';

/**
 * Real browser DOM adapter
 * Abstracts DOM operations for dependency injection and testing
 */
export class BrowserDOMAdapter implements DOMAdapter {
  querySelector<T extends HTMLElement>(selector: string): T | null {
    return document.querySelector<T>(selector);
  }

  querySelectorAll<T extends HTMLElement>(selector: string): T[] {
    return Array.from(document.querySelectorAll<T>(selector));
  }

  async waitForElement<T extends HTMLElement>(
    selector: string,
    timeoutMs: number
  ): Promise<T | null> {
    const startTime = Date.now();
    const pollInterval = 100;
    const maxIterations = Math.ceil(timeoutMs / pollInterval) + 10; // Safety margin against infinite loops
    let iterations = 0;

    while (Date.now() - startTime < timeoutMs && iterations < maxIterations) {
      iterations++;
      const element = this.querySelector<T>(selector);

      if (element && this.isVisible(element)) {
        return element;
      }

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    return null;
  }

  setAttribute(element: HTMLElement, attr: string, value: string): void {
    element.setAttribute(attr, value);
  }

  getBoundingClientRect(element: HTMLElement): DOMRect {
    return element.getBoundingClientRect();
  }

  focus(element: HTMLElement): void {
    element.focus();
  }

  click(element: HTMLElement): void {
    const selector = element.getAttribute('data-tutorial-target') || element.tagName;
    console.log(`[DOMAdapter] Clicking element:`, { selector, element });
    // Mark this as a tutorial-initiated click so TutorialOverlay doesn't block it
    (element as HTMLElement & { _tutorialClick?: boolean })._tutorialClick = true;
    element.click();
    console.log(`[DOMAdapter] Click executed on element:`, selector);
    // Clean up the flag after a tick
    setTimeout(() => {
      delete (element as HTMLElement & { _tutorialClick?: boolean })._tutorialClick;
    }, 0);
  }

  fillInput(element: HTMLInputElement | HTMLTextAreaElement, value: string): void {
    const selector = element.getAttribute('data-tutorial-target') || element.tagName;
    console.log(`[DOMAdapter] Filling input:`, { selector, value, currentValue: element.value });

    // Use React's native setter trick to trigger onChange events
    const nativeDescriptor = Object.getOwnPropertyDescriptor(
      element instanceof HTMLTextAreaElement
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype,
      'value'
    );

    const nativeInputValueSetter = nativeDescriptor?.set?.bind(element);
    if (nativeInputValueSetter) {
      nativeInputValueSetter(value);
    } else {
      element.value = value;
    }

    // For React 18+, we need to trigger events in a way that React's event delegation picks up
    // React listens for 'input' events on the document root for onChange handlers
    // We need to create a proper InputEvent (not just Event) with the right properties

    // Create a proper input event that React will recognize
    const inputEvent = new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      composed: true,
      data: value,
      inputType: 'insertText',
    });

    // Dispatch the input event which triggers React's onChange
    element.dispatchEvent(inputEvent);

    console.log(`[DOMAdapter] Input filled and event dispatched:`, { selector, newValue: element.value });

    // Verify the React state will be updated by checking after a tick
    setTimeout(() => {
      console.log(`[DOMAdapter] Input value after tick:`, { selector, value: element.value });
    }, 100);
  }

  selectOption(element: HTMLSelectElement, value: string): void {
    // Use React's native setter trick to trigger onChange events
    const nativeDescriptor = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype,
      'value'
    );

    const nativeSelectValueSetter = nativeDescriptor?.set?.bind(element);
    if (nativeSelectValueSetter) {
      nativeSelectValueSetter(value);
    } else {
      element.value = value;
    }

    // Trigger React's onChange event
    const changeEvent = new Event('change', { bubbles: true });
    element.dispatchEvent(changeEvent);
  }

  isVisible(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      style.opacity !== '0'
    );
  }

  isDisabled(element: HTMLElement): boolean {
    if ('disabled' in element) {
      return Boolean((element as HTMLButtonElement | HTMLInputElement).disabled);
    }
    return element.getAttribute('aria-disabled') === 'true';
  }

  isReadOnly(element: HTMLElement): boolean {
    if ('readOnly' in element) {
      return Boolean((element as HTMLInputElement | HTMLTextAreaElement).readOnly);
    }
    return element.getAttribute('readonly') !== null;
  }
}

/**
 * Mock DOM adapter for testing
 * Simulates DOM operations without requiring a real browser
 */
export interface MockElement {
  id: string;
  visible: boolean;
  disabled: boolean;
  rect: { top: number; left: number; width: number; height: number };
  value?: string;
  attributes: Record<string, string>;
  clickCount: number;
  focused: boolean;
}

export class MockDOMAdapter implements DOMAdapter {
  private elements = new Map<string, MockElement>();
  private defaultRect = { top: 0, left: 0, width: 100, height: 50 };

  registerElement(selector: string, props: Partial<MockElement> = {}): void {
    this.elements.set(selector, {
      id: selector,
      visible: true,
      disabled: false,
      rect: this.defaultRect,
      value: '',
      attributes: {},
      clickCount: 0,
      focused: false,
      ...props,
    });
  }

  querySelector<T extends HTMLElement>(selector: string): T | null {
    const mock = this.elements.get(selector);
    if (!mock) return null;

    // Return a minimal HTMLElement-like object
    return {
      id: mock.id,
      getAttribute: (attr: string) => mock.attributes[attr],
      setAttribute: (attr: string, value: string) => {
        mock.attributes[attr] = value;
      },
      click: () => {
        mock.clickCount++;
      },
      focus: () => {
        mock.focused = true;
      },
      value: mock.value,
      disabled: mock.disabled,
      getBoundingClientRect: () => mock.rect,
    } as unknown as T;
  }

  querySelectorAll<T extends HTMLElement>(selector: string): T[] {
    const element = this.querySelector<T>(selector);
    return element ? [element] : [];
  }

  waitForElement<T extends HTMLElement>(
    selector: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _timeoutMs: number
  ): Promise<T | null> {
    // In mock, return immediately if element exists
    return Promise.resolve(this.querySelector<T>(selector));
  }

  setAttribute(element: HTMLElement, attr: string, value: string): void {
    const mock = this.elements.get(element.id);
    if (mock) {
      mock.attributes[attr] = value;
    }
  }

  getBoundingClientRect(element: HTMLElement): DOMRect {
    const mock = this.elements.get(element.id);
    const rect = mock?.rect || this.defaultRect;
    return {
      ...rect,
      right: rect.left + rect.width,
      bottom: rect.top + rect.height,
      x: rect.left,
      y: rect.top,
      toJSON: () => rect,
    };
  }

  focus(element: HTMLElement): void {
    const mock = this.elements.get(element.id);
    if (mock) {
      mock.focused = true;
    }
  }

  click(element: HTMLElement): void {
    const mock = this.elements.get(element.id);
    if (mock) {
      mock.clickCount++;
    }
  }

  fillInput(element: HTMLInputElement | HTMLTextAreaElement, value: string): void {
    const mock = this.elements.get(element.id);
    if (mock) {
      mock.value = value;
    }
  }

  selectOption(element: HTMLSelectElement, value: string): void {
    const mock = this.elements.get(element.id);
    if (mock) {
      mock.value = value;
    }
  }

  isVisible(element: HTMLElement): boolean {
    const mock = this.elements.get(element.id);
    return mock?.visible ?? true;
  }

  isDisabled(element: HTMLElement): boolean {
    const mock = this.elements.get(element.id);
    return mock?.disabled ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isReadOnly(_element: HTMLElement): boolean {
    // Mock implementation - could be extended if needed for testing
    return false;
  }

  // Test helpers
  getClickCount(selector: string): number {
    return this.elements.get(selector)?.clickCount ?? 0;
  }

  isFocused(selector: string): boolean {
    return this.elements.get(selector)?.focused ?? false;
  }

  getValue(selector: string): string | undefined {
    return this.elements.get(selector)?.value;
  }
}
